/**
 * Esquemas Zod del formulario de cotización (`docs/frontend_conventions.md`, Validación).
 *
 * El formulario valida ANTES de llamar a `POST /cotizador/calcular`, igual que el SSR
 * (que hacía `alert('Por favor ingrese medidas válidas')` antes del fetch) y como espera el
 * backend (`Medidas inválidas` -> 400). Las medidas deben ser positivas; en modo "diferentes"
 * cada puerta tiene su par ancho/alto. Los tipos del formulario se **infieren** del esquema.
 *
 * Solo se valida lo que el cliente puede comprobar (números positivos, cantidad, medidas
 * completas). El resto de campos (modelo, calibre, modo de cobro…) son selecciones acotadas
 * por la UI; el motor del backend valida el cálculo en sí.
 */
import { z } from "zod";

/** Una medida positiva (ancho o alto en cm); réplica del `min=1`/`> 0` del SSR. */
const positiveMeasure = z
  .number({ invalid_type_error: "Ingrese un número." })
  .positive("Debe ser mayor que 0.");

/** Una fila de medida por puerta (modo "diferentes"). */
export const doorMeasureSchema = z.object({
  ancho: positiveMeasure,
  alto: positiveMeasure,
});

/**
 * Esquema del formulario de cotización. Valida según el modo de medidas:
 *  - iguales: `ancho` y `alto` deben venir y ser positivos.
 *  - diferentes: `medidas` debe tener exactamente `cantidad` filas válidas.
 *
 * El `superRefine` reproduce la regla del SSR/backend (medidas completas para todas las
 * puertas) y adjunta el error al campo correcto para mostrarlo junto al input.
 */
export const cotizadorFormSchema = z
  .object({
    modelo_id: z.string().min(1),
    calibre: z.string().min(1),
    cantidad: z
      .number({ invalid_type_error: "Ingrese una cantidad." })
      .int("Debe ser un entero.")
      .min(1, "Mínimo 1 puerta."),
    chapa_bola: z.boolean(),
    venas: z.number().int().min(1),
    ancho_vena: z.number().positive(),
    sub_modelo_dv: z.number().int(),
    calibre_marco: z.string().min(1),
    verona_estilo: z.string().min(1),
    modo_cobro: z.string().min(1),
    medidas_diferentes: z.boolean(),
    // En modo "iguales" se usan estos; opcionales aquí y exigidos por el refine.
    ancho: z.number().optional(),
    alto: z.number().optional(),
    // En modo "diferentes" se usa este; validado por el refine.
    medidas: z.array(doorMeasureSchema).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.medidas_diferentes) {
      const medidas = value.medidas ?? [];
      if (medidas.length !== value.cantidad) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["medidas"],
          message: `Complete las medidas de las ${value.cantidad} puertas.`,
        });
        return;
      }
      medidas.forEach((m, idx) => {
        if (!(m.ancho > 0) || !(m.alto > 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["medidas", idx],
            message: `Ingrese medidas válidas para la puerta ${idx + 1}.`,
          });
        }
      });
      return;
    }
    // Modo "iguales": ancho y alto obligatorios y positivos.
    if (value.ancho === undefined || !(value.ancho > 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ancho"],
        message: "Ingrese un ancho válido.",
      });
    }
    if (value.alto === undefined || !(value.alto > 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["alto"],
        message: "Ingrese un alto válido.",
      });
    }
  });

/** Valores del formulario de cotización (inferidos del esquema). */
export type CotizadorFormValues = z.infer<typeof cotizadorFormSchema>;
